import React, { useRef, useEffect, useState } from 'react';
import { createChart } from 'lightweight-charts';

// Format price based on value: 6 significant digits for $10 and under, 2 decimal places for over $10
const formatPrice = (price) => {
  if (price == null || isNaN(price)) return '0.00';
  if (price > 10) {
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else {
    return price.toLocaleString('en-US', { maximumSignificantDigits: 6 });
  }
};

function PriceChart({ coinId, currentPrice }) {
  const chartContainerRef = useRef();
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const loadedDataRef = useRef({ earliest: null, latest: null });
  const isLoadingMoreRef = useRef(false);
  const activeCoinRef = useRef(coinId); // guard stale fetches

  const [chartInterval, setChartInterval] = useState('1h');
  const [chartType, setChartType] = useState('Candle');
  const [ohlcData, setOhlcData] = useState({ open: 0, high: 0, low: 0, close: 0, volume: 0 });
  const [loading, setLoading] = useState(false);

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      autoSize: true,
      layout: {
        background: { color: '#1a1a1a' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: '#2a2a2a' },
        horzLines: { color: '#2a2a2a' },
      },
      crosshair: {
        mode: 1,
      },
      rightPriceScale: {
        borderColor: '#2a2a2a',
      },
      localization: {
        priceFormatter: formatPrice,
      },
      timeScale: {
        borderColor: '#2a2a2a',
        timeVisible: true,
        secondsVisible: false,
      },
      height: 515,
    });

    chartRef.current = chart;

    return () => {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, []);

  // Subscribe to visible range changes to detect zoom-out
  useEffect(() => {
    if (!chartRef.current) return;

    const handleVisibleRangeChange = (newVisibleRange) => {
      if (!newVisibleRange || !loadedDataRef.current.earliest) return;

      const { from } = newVisibleRange;
      const loadedEarliest = loadedDataRef.current.earliest;
      const threshold = 0.2;
      const dataRange = loadedDataRef.current.latest - loadedDataRef.current.earliest;
      const bufferZone = loadedEarliest + (dataRange * threshold);

      if (from <= bufferZone && !isLoadingMoreRef.current) {
        fetchMoreHistoricalData();
      }
    };

    const unsubscribe = chartRef.current.timeScale().subscribeVisibleTimeRangeChange(handleVisibleRangeChange);

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [coinId, chartInterval]);

  // Update chart type and series
  useEffect(() => {
    if (!chartRef.current) return;

    if (seriesRef.current) {
      chartRef.current.removeSeries(seriesRef.current);
    }

    const customPriceFormat = {
      type: 'custom',
      formatter: formatPrice,
      minMove: 0.000001,
    };

    let newSeries;
    if (chartType === 'Candle') {
      newSeries = chartRef.current.addCandlestickSeries({
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: false,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350',
        priceFormat: customPriceFormat,
      });
    } else if (chartType === 'Line') {
      newSeries = chartRef.current.addLineSeries({
        color: '#2962FF',
        lineWidth: 2,
        priceFormat: customPriceFormat,
      });
    } else if (chartType === 'Area') {
      newSeries = chartRef.current.addAreaSeries({
        topColor: 'rgba(41, 98, 255, 0.56)',
        bottomColor: 'rgba(41, 98, 255, 0.04)',
        lineColor: 'rgba(41, 98, 255, 1)',
        lineWidth: 2,
        priceFormat: customPriceFormat,
      });
    }

    seriesRef.current = newSeries;

    if (coinId) {
      fetchChartData(coinId, chartInterval);
    }
  }, [chartType]);

  // Fetch chart data when coinId or interval changes
  useEffect(() => {
    activeCoinRef.current = coinId;
    if (coinId && seriesRef.current) {
      // Keep old series data visible while loading — clearing to [] causes a
      // black canvas on Chrome when the fetch is slow or gets discarded.
      loadedDataRef.current = { earliest: null, latest: null };
      fetchChartData(coinId, chartInterval, true);
    }
  }, [coinId, chartInterval]);

  const fetchChartData = async (requestedCoinId, requestedInterval, isInitialLoad = true) => {
    if (!requestedCoinId) return;

    if (isInitialLoad) setLoading(true);

    try {
      const response = await fetch(`/api/crypto/klines/${requestedCoinId}?interval=${requestedInterval}`);
      const result = await response.json();

      // Discard if the user switched coins while this fetch was in flight
      if (activeCoinRef.current !== requestedCoinId) return;

      if (result.success && seriesRef.current) {
        const formattedData = result.data.map(candle => ({
          time: candle.time / 1000,
          open: candle.open,
          high: candle.high,
          low: candle.low,
          close: candle.close,
          value: candle.close,
        }));

        seriesRef.current.setData(formattedData);

        if (formattedData.length > 0) {
          loadedDataRef.current = {
            earliest: formattedData[0].time,
            latest: formattedData[formattedData.length - 1].time,
          };
        }

        if (result.data.length > 0) {
          const lastCandle = result.data[result.data.length - 1];
          setOhlcData({
            open: lastCandle.open,
            high: lastCandle.high,
            low: lastCandle.low,
            close: lastCandle.close,
            volume: lastCandle.volume,
          });
        }

        if (isInitialLoad && chartRef.current) {
          chartRef.current.timeScale().fitContent();
        }
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      if (isInitialLoad) setLoading(false);
    }
  };

  const fetchMoreHistoricalData = async () => {
    const requestedCoinId = activeCoinRef.current;
    if (!requestedCoinId || !seriesRef.current || isLoadingMoreRef.current) return;

    isLoadingMoreRef.current = true;

    try {
      const earliestTime = loadedDataRef.current.earliest;

      const intervalToSeconds = {
        '1m': 60, '5m': 300, '15m': 900,
        '1h': 3600, '4h': 14400, '1d': 86400,
      };
      const intervalSeconds = intervalToSeconds[chartInterval] || 300;
      const endTime = (earliestTime - intervalSeconds) * 1000;

      const ninetyDaysAgo = Date.now() - (90 * 24 * 60 * 60 * 1000);
      if (endTime < ninetyDaysAgo) {
        isLoadingMoreRef.current = false;
        return;
      }

      const response = await fetch(`/api/crypto/klines/${requestedCoinId}?interval=${chartInterval}&endTime=${endTime}`);
      const result = await response.json();

      // Discard if coin switched while fetching
      if (activeCoinRef.current !== requestedCoinId) return;

      if (result.success && result.data.length > 0) {
        const formattedData = result.data.map(candle => ({
          time: candle.time / 1000,
          open: candle.open,
          high: candle.high,
          low: candle.low,
          close: candle.close,
          value: candle.close,
        }));

        const newData = formattedData.filter(d => d.time < loadedDataRef.current.earliest);

        if (newData.length > 0) {
          const existingData = seriesRef.current.data();

          if (existingData.length > 0) {
            const priceChange = Math.abs(
              (newData[newData.length - 1].close - existingData[0].close) / existingData[0].close
            );
            if (priceChange > 2.0) {
              isLoadingMoreRef.current = false;
              return;
            }
          }

          const mergedData = [...newData, ...existingData].sort((a, b) => a.time - b.time);
          seriesRef.current.setData(mergedData);
          loadedDataRef.current.earliest = newData[0].time;
        }
      }
    } catch (error) {
      console.error('Error fetching more historical data:', error);
    } finally {
      isLoadingMoreRef.current = false;
    }
  };

  const handleIntervalChange = (e) => {
    setChartInterval(e.target.value);
  };

  const handleChartTypeChange = (e) => {
    setChartType(e.target.value);
  };

  return (
    <div className="price-chart-container">
      <div className="chart-header">
        <h3>Price Charts</h3>

        <div className="chart-controls">
          {/* Interval selector */}
          <select value={chartInterval} onChange={handleIntervalChange}>
            <option value="1m">1m</option>
            <option value="5m">5m</option>
            <option value="15m">15m</option>
            <option value="1h">1h</option>
            <option value="4h">4h</option>
            <option value="1d">1d</option>
          </select>

          {/* Chart type selector */}
          <select value={chartType} onChange={handleChartTypeChange}>
            <option value="Candle">Candle</option>
            <option value="Line">Line</option>
            <option value="Area">Area</option>
          </select>
        </div>
      </div>

      {/* Chart data display */}
      <div className="chart-data-bar">
        <span>O: {formatPrice(ohlcData.open)}</span>
        <span>H: {formatPrice(ohlcData.high)}</span>
        <span>L: {formatPrice(ohlcData.low)}</span>
        <span>C: {formatPrice(ohlcData.close)}</span>
        <span>V: {ohlcData.volume.toFixed(2)}</span>
      </div>

      {/* TradingView Lightweight Charts */}
      {loading && <div className="chart-loading">Loading chart data...</div>}
      <div ref={chartContainerRef} className="chart-canvas" />

    </div>
  );
}

export default PriceChart;
